/**
 * CalendarEventInfoDialog
 *
 * Dialog read-only para exibir detalhes de eventos do Calendário Global
 * cuja fonte ainda não possui modal dedicado (booking, google) ou quando
 * o usuário não tem permissão para editar um evento manual.
 *
 * RELEASE:
 *   2026-10-09 v1.2.0 - Reconhece Ordem de Serviço no catálogo de fontes.
 *   2026-08-07 v1.1.0 - Indicador "Sincronizado com o Google" + link direto
 *                       para o evento no Google Agenda (quando há
 *                       google_event_id). Evita a dúvida de "criei aqui e
 *                       não sei se foi para o Google".
 *   2026-06-24 v1.0.0 - Versão inicial (fallback do CalendarEventModalRouter)
 */


import { sanitizeHtml } from '@/lib/sanitize';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Video, Clock, ExternalLink, CheckCircle2 } from 'lucide-react';
import type { UnifiedCalendarEvent } from '@/hooks/useCalendarUnifiedEvents';

interface CalendarEventInfoDialogProps {
  event: UnifiedCalendarEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SOURCE_LABEL: Record<UnifiedCalendarEvent['source_type'], string> = {
  manual: 'Evento manual',
  booking: 'Agendamento Online',
  activity: 'Atividade CRM',
  task: 'Tarefa',
  service_order: 'Ordem de Serviço',
  google: 'Google Calendar',
  scheduled_message: 'Mensagem agendada',
};

/** Formata intervalo de datas em pt-BR. */
function formatRange(startIso: string, endIso: string, allDay: boolean): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (allDay) {
    return start.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }
  const date = start.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${fmtTime(start)} – ${fmtTime(end)}`;
}

export function CalendarEventInfoDialog({
  event,
  open,
  onOpenChange,
}: CalendarEventInfoDialogProps) {
  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="inline-block h-3 w-3 rounded-full shrink-0"
              style={{ backgroundColor: event.calendar_color }}
              aria-hidden
            />
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
              {SOURCE_LABEL[event.source_type]}
            </Badge>
            <span className="text-xs text-muted-foreground truncate">
              {event.calendar_name}
            </span>
          </div>
          <DialogTitle className="text-xl pt-2">{event.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-2">
            <Clock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <span className="capitalize">
              {formatRange(event.start_at, event.end_at, event.all_day)}
            </span>
          </div>

          {event.location && (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <span>{event.location}</span>
            </div>
          )}

          {event.meet_link && (
            <div className="flex items-start gap-2">
              <Video className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <a
                href={event.meet_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {event.meet_link}
              </a>
            </div>
          )}

          {/* Descrição: renderiza HTML sanitizado quando existir (rich text) */}
          {(event.description_html || event.description) && (
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              {event.description_html ? (
                <div
                  className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground [&_a]:text-primary"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description_html) }}
                />
              ) : (
                <p className="whitespace-pre-wrap text-muted-foreground">
                  {event.description}
                </p>
              )}
            </div>
          )}

          {/* Indicador de sincronização com o Google Agenda.
              Só aparece quando o evento já possui google_event_id, ou seja,
              quando ele realmente existe na conta Google vinculada. */}
          {event.google_event_id && (
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs">Sincronizado com o Google Agenda</p>
                <a
                  href={`https://calendar.google.com/calendar/u/0/r/day/${new Date(event.start_at).getFullYear()}/${new Date(event.start_at).getMonth() + 1}/${new Date(event.start_at).getDate()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Abrir no Google Agenda <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}

        </div>


        <DialogFooter>
          {event.meet_link && (
            <Button
              variant="default"
              onClick={() => window.open(event.meet_link!, '_blank', 'noopener,noreferrer')}
            >
              <Video className="h-4 w-4 mr-2" /> Abrir Meet
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
